import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, TextField, MenuItem, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Edit } from '@mui/icons-material';
import AppointmentModal, { AppointmentData } from './modals/AppointmentModal';
import { useSchedulePermissions } from '../hooks/useSchedulePermissions';
import { useRouter } from '../contexts/RouterContext';
import StandardPagination from './Pagination/StandardPagination';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  schedulesService,
  mapScheduleError,
  RescheduleSeriesPayload,
  ScheduleItem,
  ScheduleOptions,
  ScheduleSeriesItem,
  PatientScheduleProfessional,
} from '../services/schedulesService';
import { scheduleBlocksService } from "../services/scheduleBlocksService";
import { patientsService } from '../services/patientsService';
import { holidaysService } from '../services/holidaysService';
import { colors, typography } from '../theme/designSystem';
import { filterFieldSx, searchFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';
import ClearFiltersButton from './ClearFiltersButton';
import { toLocalDateStr, shiftMonthsLocalDateStr, shiftDaysLocalDateStr } from '../utils/datetime';

/**
 * Aba Agenda do cadastro do paciente (RN-533): agendamentos reais do paciente, do mais recente
 * ao mais antigo, com filtros reativos e paginação no servidor. O lápis abre o MESMO modal da
 * agenda em edição (série, histórico, exclusão, janela de edição); o "+" abre em criação com
 * o paciente travado. Escopo 'own' (perfil Profissional): só altera a própria agenda e só
 * agenda para si — o servidor recusa o resto; a tela apenas não oferece o que seria recusado.
 */
interface Props {
  patientId: string;
  patientName: string;
  entityId: string;
}

const DEFAULT_PAGE_SIZE = 10;
const FALLBACK_STATUS_COLOR = '#6c757d';
// Janela padrão da lista (decisão do Hilton, 30/08/2026): 3 meses para trás e 3 à frente; as
// datas seguem livres — "ver todo o histórico" esvazia as duas
const DEFAULT_PAST_MONTHS = 3;
// Data final padrão = AMANHÃ (D+1, decisão do Hilton 30/08): +3 meses enchia o topo da
// lista com as sessões futuras da série, todas "Pendente confirmação"
const DEFAULT_FUTURE_DAYS = 1;

const money = (v: number | null): string => (v == null ? '' : v.toFixed(2));

// Datas vêm no relógio da entidade (ISO sem offset) — formatar pelas partes, sem new Date
const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

// Legado com duração zero mostra só o início (mesma regra da lista da série na agenda)
const timeRange = (item: ScheduleItem): string => {
  const start = timeOf(item.dateTimeInitial);
  const end = timeOf(item.dateTimeFinal);
  return end && end !== start ? `${start} - ${end}` : start;
};

// Fim para o modal = início + duração real; legado sem fim cai em 30 min (como na agenda)
const endTimeForModal = (item: ScheduleItem): string => {
  const minutes = Math.round(
    (new Date(item.dateTimeFinal).getTime() - new Date(item.dateTimeInitial).getTime()) / 60000);
  const [h, m] = timeOf(item.dateTimeInitial).split(':').map(Number);
  const total = h * 60 + m + (minutes > 0 ? minutes : 30);
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const fieldSx = {
  backgroundColor: '#fff',
  flex: '1 1 170px',
  minWidth: 150,
  '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '40px' },
};

const Dot: React.FC = () => (
  <Typography component="span" sx={{ color: colors.textSecondary, fontSize: '0.9rem' }}>•</Typography>
);

const PatientScheduleTab: React.FC<Props> = ({ patientId, patientName, entityId }) => {
  const [options, setOptions] = useState<ScheduleOptions | null>(null);
  const [optionsError, setOptionsError] = useState('');

  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({ untilToday: 0, afterToday: 0 });
  const [professionals, setProfessionals] = useState<PatientScheduleProfessional[]>([]);
  const [loading, setLoading] = useState(true);
  const { getParam } = useRouter();
  // Lido na montagem, direto da URL: o roteador pode reescrever a query depois
  const deepLinkScheduleId = React.useRef<string | null>(new URLSearchParams(window.location.search).get('scheduleId') || getParam('scheduleId') || null);
  const openedFromUrl = React.useRef(false);
  const [error, setError] = useState('');

  const [defaultDates] = useState(() => ({
    from: shiftMonthsLocalDateStr(-DEFAULT_PAST_MONTHS),
    to: shiftDaysLocalDateStr(DEFAULT_FUTURE_DAYS),
  }));
  const [professionalId, setProfessionalId] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState(defaultDates.from);
  const [to, setTo] = useState(defaultDates.to);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [modalData, setModalData] = useState<Partial<AppointmentData>>({});
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [seriesItems, setSeriesItems] = useState<ScheduleSeriesItem[]>([]);

  const { toast, showToast, hideToast } = useToast();
  // Descarta respostas fora de ordem (filtros trocados em sequência rápida)
  const loadSeqRef = useRef(0);

  // Combos e catálogos da agenda (uma vez): sem eles o modal cairia nas listas de exemplo,
  // então criar/editar ficam desabilitados até carregarem
  useEffect(() => {
    schedulesService.getOptions()
      .then(setOptions)
      .catch(() => setOptionsError('Não foi possível carregar os dados da agenda. Recarregue a página.'));
  }, []);

  const invalidRange = !!from && !!to && to < from;

  const load = useCallback(async () => {
    if (invalidRange) {
      setItems([]);
      setTotalCount(0);
      setError('A data final não pode ser anterior à data inicial.');
      setLoading(false);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const result = await schedulesService.getByPatient(patientId, {
        professionalId: professionalId || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        pageNumber: page,
        pageSize,
      });
      if (seq !== loadSeqRef.current) return;
      setItems(result.items);
      setTotalCount(result.totalCount);
      setTotals({ untilToday: result.totalUntilToday ?? 0, afterToday: result.totalAfterToday ?? 0 });
      setProfessionals(result.professionals);
    } catch {
      if (seq !== loadSeqRef.current) return;
      setError('Não foi possível carregar a agenda do paciente.');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [patientId, professionalId, status, from, to, page, pageSize, invalidRange]);

  useEffect(() => {
    load();
  }, [load]);

  // Filtros reativos: qualquer mudança volta para a primeira página
  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };
  const isDefaultWindow = from === defaultDates.from && to === defaultDates.to;
  const hasFilters = !!professionalId || !!status || !isDefaultWindow;
  // Limpar = voltar ao padrão (janela ±3 meses), não "sem datas"
  const clearFilters = () => {
    setProfessionalId('');
    setStatus('');
    setFrom(defaultDates.from);
    setTo(defaultDates.to);
    setPage(1);
  };
  const showWholeHistory = () => {
    setFrom('');
    setTo('');
    setPage(1);
  };

  // Status do filtro e cor das pílulas vêm do CATÁLOGO (confirmação + presença) — data-driven
  const statusCatalog = useMemo(
    () => [...(options?.confirmationStatuses ?? []), ...(options?.presenceStatuses ?? [])],
    [options]);
  const statusOptions = useMemo(
    () => Array.from(new Set(statusCatalog.map(s => s.name))), [statusCatalog]);
  const statusColor = (item: ScheduleItem): string =>
    statusCatalog.find(s => s.name === item.statusDisplay)?.color || FALLBACK_STATUS_COLOR;

  const isOwnScope = options?.dataScope === 'own';
  const schedulePerms = useSchedulePermissions();
  const ownProfessional = isOwnScope
    ? (options?.professionals ?? []).find(p => (options?.ownProfessionalIds ?? []).includes(p.id)) ?? null
    : null;
  // Perfil Profissional não altera agenda de outro profissional (RN-533b); e só quem altera
  // agendamento (ou só confirmação/presença) tem o botão de editar (RN-115d)
  const canEdit = (item: ScheduleItem): boolean => schedulePerms.canOpenForChange
    && (!isOwnScope || (!!item.professionalId && (options?.ownProfessionalIds ?? []).includes(item.professionalId)));

  const toModalData = (item: ScheduleItem): Partial<AppointmentData> => ({
    patient: item.patientName || patientName,
    patientId: item.patientId,
    firstResponsible: '',
    secondResponsible: '',
    professional: item.professionalName || '',
    team: item.teamName || '',
    serviceType: item.serviceTypeName || '',
    serviceTypeId: item.serviceTypeId,
    startDate: item.dateTimeInitial.slice(0, 10),
    startTime: timeOf(item.dateTimeInitial),
    endDate: item.dateTimeInitial.slice(0, 10),
    endTime: endTimeForModal(item),
    appointmentType: (item.occurrences ?? 1) > 1 ? 'recurring' : 'unique',
    recurrenceType: '',
    maxOccurrences: item.occurrences ?? 1,
    // Edição espelha o GRAVADO (valores nunca recalculados aqui)
    unitValue: money(item.eventValueUnit),
    discount: money(item.eventValueUnitDiscount ?? 0),
    totalUnit: money(item.eventValueTotalUnit),
    totalValue: money(item.eventValueTotalAll),
    observations: item.notes || '',
    confirmationStatus: item.patientConfirmation || '',
    attendanceStatus: item.patientPresence || '',
    cancellationReason: item.reasonToCancellation || '',
  });

  const openEdit = (item: ScheduleItem) => {
    setSelectedItem(item);
    setSeriesItems([]);
    if ((item.occurrences ?? 1) > 1) {
      schedulesService.getSeries(item.id)
        .then(setSeriesItems)
        .catch(() => setSeriesItems([]));
    }
    setModalData(toModalData(item));
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setModalData({});
    setSeriesItems([]);
  };

  // Navegação entre ocorrências da série DENTRO do modal
  const openOccurrenceById = async (scheduleId: string) => {
    try {
      openEdit(await schedulesService.getById(scheduleId));
    } catch {
      showToast('Não foi possível abrir o agendamento.', 'error');
    }
  };

  // Deep link do caixa (?scheduleId=): abre o agendamento quando lista e opções já carregaram
  useEffect(() => {
    const sid = deepLinkScheduleId.current;
    if (!sid || loading || !options || openedFromUrl.current) return;
    openedFromUrl.current = true;
    openOccurrenceById(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, options]);

  // Gravação real (RN-302): o modal aguarda a Promise — overlay aberto, erro sem fechar
  const handleSave = async (data: AppointmentData) => {
    if (!data.professionalId || !data.serviceTypeId) {
      throw new Error('Selecione profissional e serviço.');
    }
    if (!data.startDate || !data.startTime || !data.endTime) {
      throw new Error('Informe a data e os horários do agendamento.');
    }

    if (modalMode === 'edit') {
      if (!selectedItem) return;
      try {
        const result = await schedulesService.updateSchedule(selectedItem.id, {
          professionalRegistrationId: data.professionalId,
          teamId: data.teamId ?? null,
          serviceTypeId: data.serviceTypeId,
          startDate: data.startDate.slice(0, 10),
          startTime: data.startTime,
          endTime: data.endTime,
          confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
          presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
          notes: data.observations ? data.observations : null,
          cancellationReason: data.cancellationReason ? data.cancellationReason : null,
          applyToSeries: data.applyToSeries ?? false,
          roomId: data.roomId ?? '',
        });
        showToast(result.updated > 1
          ? `${result.updated} agendamentos atualizados.`
          : 'Agendamento atualizado.', 'success');
        load();
        // O modal fica aberto após salvar: rebaseia com o GRAVADO para zerar o dirty e
        // atualizar série/histórico
        try {
          openEdit(await schedulesService.getById(selectedItem.id));
        } catch {
          // Sem a recarga o modal segue utilizável — só não rebaseia o formulário
        }
      } catch (err: any) {
        throw new Error(mapScheduleError(err));
      }
      return;
    }

    const recurring = data.appointmentType === 'recurring';
    if (recurring && !data.recurrenceType) {
      throw new Error('Escolha a periodicidade da recorrência.');
    }
    try {
      const result = await schedulesService.createSchedule({
        patientId,
        professionalRegistrationId: data.professionalId,
        teamId: data.teamId ?? null,
        serviceTypeId: data.serviceTypeId,
        startDate: data.startDate.slice(0, 10),
        startTime: data.startTime,
        endTime: data.endTime,
        recurring,
        recurrenceType: recurring ? data.recurrenceType : null,
        occurrences: recurring ? data.maxOccurrences : 1,
        notes: data.observations ? data.observations : null,
        confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
        presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
        skippedDates: recurring ? data.skippedDates ?? [] : [],
        roomId: data.roomId ?? null,
        slots: recurring && data.slots && data.slots.length > 0 ? data.slots : undefined,
      });
      showToast(result.ids.length > 1
        ? `${result.ids.length} agendamentos gravados.`
        : 'Agendamento gravado.', 'success');
      // O novo aparece no topo: volta para a primeira página (o efeito recarrega)
      if (page === 1) load(); else setPage(1);
    } catch (err: any) {
      throw new Error(mapScheduleError(err));
    }
  };

  const handleDelete = async (opts: { reason: string; includeFollowing: boolean }) => {
    if (!selectedItem) return;
    try {
      const result = await schedulesService.deleteSchedule(selectedItem.id, opts);
      const kept = result.skippedProtected > 0
        ? ` ${result.skippedProtected} mantido(s) por status ou pagamento.`
        : '';
      showToast(result.deleted > 1
        ? `${result.deleted} agendamentos excluídos.${kept}`
        : `Agendamento excluído.${kept}`, 'success');
      load();
    } catch (err: any) {
      throw new Error(mapScheduleError(err));
    }
  };

  // Lista da série editável (RN-118m)
  const handleRescheduleSeries = async (payload: RescheduleSeriesPayload) => {
    if (!selectedItem) return;
    try {
      const result = await schedulesService.rescheduleSeries(selectedItem.id, payload);
      const parts = [
        result.moved > 0 ? `${result.moved} ${result.moved === 1 ? 'sessão remanejada' : 'sessões remanejadas'}` : '',
        result.deleted > 0 ? `${result.deleted} ${result.deleted === 1 ? 'removida' : 'removidas'}` : '',
      ].filter(Boolean);
      showToast(`${parts.join(' e ')}.`.replace(/^./, c => c.toUpperCase()), 'success');
      setSeriesItems(await schedulesService.getSeries(selectedItem.id));
      load();
    } catch (err: any) {
      throw new Error(mapScheduleError(err));
    }
  };

  const loadPatientDiscounts = useCallback(
    async () => schedulesService.getPatientDiscounts(patientId), [patientId]);

  const loadPatientInfo = useCallback(async () => {
    const patient = await patientsService.getById(patientId);
    return { responsible1: patient.responsibleName1 ?? null, responsible2: patient.responsibleName2 ?? null };
  }, [patientId]);

  const loadHolidaysOnDate = useCallback(async (date: string) => {
    if (!entityId) return [];
    const rows = await holidaysService.getOnDate(entityId, date);
    return rows.map(h => h.holidayTitle);
  }, [entityId]);

  const loadHolidayCalendar = useCallback(async () => {
    if (!entityId) return {};
    const rows = await holidaysService.getByEntity(entityId, false);
    return rows.reduce<Record<string, string>>((map, h) => {
      if (h.active) map[h.day] = h.holidayTitle;
      return map;
    }, {});
  }, [entityId]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="tab-content-section">

      {/* Filtros reativos + novo agendamento */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <TextField
          select
          size="small"
          label="Profissional"
          value={professionalId}
          onChange={(e) => changeFilter(setProfessionalId)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {professionals.map(p => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => changeFilter(setStatus)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {statusOptions.map(name => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          size="small"
          label="De"
          value={from}
          onChange={(e) => changeFilter(setFrom)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          type="date"
          size="small"
          label="Até"
          value={to}
          onChange={(e) => changeFilter(setTo)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
      </Box>

      {optionsError && (
        <Typography sx={{ color: colors.error, fontSize: '0.875rem', mb: 2 }}>{optionsError}</Typography>
      )}

      {isOwnScope && (
        <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mb: 1.5, px: 1 }}>
          Você altera apenas os <b>seus</b> agendamentos com este paciente; os dos demais profissionais são somente consulta.
        </Typography>
      )}

      <Box sx={{ mb: 2, px: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <strong>{totalCount}</strong> {totalCount === 1 ? 'agendamento encontrado' : 'agendamentos encontrados'} no período/filtros atuais
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : error ? (
        <Typography sx={{ color: colors.error, fontSize: '0.9rem', px: 1, py: 2 }}>{error}</Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', textAlign: 'center', py: 4 }}>
          Nenhum agendamento encontrado para este paciente.
        </Typography>
      ) : (
        <div className="appointments-list">
          {items.map(item => {
            const editable = canEdit(item);
            const cancelled = (item.statusDisplay || '').startsWith('Cancelado');
            return (
              <Box
                key={item.id}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  borderLeft: `5px solid ${statusColor(item)}`,
                  mb: 2,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: colors.text }}>
                        {formatIsoDate(item.dateTimeInitial)}
                      </Typography>
                      <Dot />
                      <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
                        {timeRange(item)}
                      </Typography>
                      <Dot />
                      <Typography sx={{ color: colors.text, fontSize: '0.9rem' }}>
                        {item.professionalName || '(sem profissional)'}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: statusColor(item),
                          color: '#fff',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.statusDisplay || item.patientConfirmation || 'Sem status'}
                      </Box>
                    </Box>
                    <Typography sx={{ color: colors.text, lineHeight: 1.6, fontSize: '0.875rem', overflowWrap: 'anywhere' }}>
                      <strong>{item.serviceTypeName || item.subject || 'Serviço não informado'}</strong>
                      {item.notes ? ` - ${item.notes}` : ''}
                    </Typography>
                    {cancelled && item.reasonToCancellation && (
                      <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: 0.5, overflowWrap: 'anywhere' }}>
                        Motivo: {item.reasonToCancellation}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip
                    title={!editable
                      ? 'Agenda de outro profissional — somente ele altera'
                      : item.locked ? 'Ver agendamento (fora da janela de edição)' : 'Editar agendamento'}
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!editable || !options}
                        onClick={() => openEdit(item)}
                        sx={{
                          color: '#2196f3',
                          border: '1px solid #e3f2fd',
                          width: 32,
                          height: 32,
                          '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#2196f3' },
                          '&.Mui-disabled': { color: '#b0bec5', borderColor: '#eceff1' },
                        }}
                      >
                        <Edit sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </div>
      )}

      {totalCount > 0 && !error && (
        <StandardPagination
          currentPage={page}
          totalPages={totalPages}
          itemsPerPage={pageSize}
          totalItems={totalCount}
          onPageChange={setPage}
          onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
        />
      )}

      {/* Mesmo modal da agenda (criar/editar/excluir com RN-302 real) */}
      <AppointmentModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        mode={modalMode}
        initialData={modalData}
        title={modalMode === 'create' ? 'Agendamento' : 'Editar Agendamento'}
        patientsList={[patientName]}
        businessHours={options?.businessHours}
        disablePatientField
        professionalsList={(options?.professionals ?? []).filter(p => p.active).map(p => p.name)}
        teamsList={(options?.teams ?? []).map(t => t.name)}
        serviceTypesList={(options?.serviceTypes ?? []).map(s => s.name)}
        professionalOptions={(options?.professionals ?? []).filter(p => p.active)}
        roomOptions={options?.rooms ?? []}
        teamOptions={options?.teams ?? []}
        serviceTypeOptions={options?.serviceTypes ?? []}
        loadPatientDiscounts={loadPatientDiscounts}
        loadPatientInfo={loadPatientInfo}
        loadHolidaysOnDate={loadHolidaysOnDate}
        loadBlocks={(professionalId, from, to) => scheduleBlocksService.getBlocks(from, to, professionalId)}
        loadConflicts={({ professionalId, patient, from, to }) => schedulesService.findConflicts(
          professionalId,
          patientId,
          from, to)}
        loadHolidayCalendar={loadHolidayCalendar}
        confirmationOptions={(options?.confirmationStatuses ?? []).map(s => s.name)}
        presenceOptions={(options?.presenceStatuses ?? []).map(s => s.name)}
        terminalConfirmations={(options?.confirmationStatuses ?? []).filter(s => s.isTerminal).map(s => s.name)}
        showPricing={!isOwnScope}
        ownProfileLock={isOwnScope}
        paid={modalMode === 'edit' && (selectedItem?.paid ?? false)}
        paymentDate={modalMode === 'edit' ? selectedItem?.paymentDate ?? null : null}
        paymentFinalValue={modalMode === 'edit' ? selectedItem?.paymentFinalValue ?? null : null}
        paymentDiscount={modalMode === 'edit' ? selectedItem?.paymentDiscount ?? null : null}
        seriesItems={modalMode === 'edit' ? seriesItems : undefined}
        currentScheduleId={modalMode === 'edit' ? selectedItem?.id ?? null : null}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        canDelete={schedulePerms.canDelete}
        locked={modalMode === 'edit' && (selectedItem?.locked ?? false)}
        attendanceOnly={modalMode === 'edit' && !schedulePerms.canEdit && schedulePerms.canAttendance}
        statusLocked={modalMode === 'edit' && !schedulePerms.canAttendance}
        rescheduleCount={modalMode === 'edit' ? selectedItem?.numReschedule ?? 0 : 0}
        loadHistory={modalMode === 'edit' && selectedItem
          ? () => schedulesService.getHistory(selectedItem.id)
          : undefined}
        onOpenOccurrence={modalMode === 'edit' ? openOccurrenceById : undefined}
        onRescheduleSeries={modalMode === 'edit' && schedulePerms.canEdit ? handleRescheduleSeries : undefined}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

export default PatientScheduleTab;
